import React, {useState, useMemo} from 'react';
import styled from 'styled-components';
import {
	DropdownList,
	FlexRow,
	tokensForThemeProvider,
	EMPHASIS,
	Icon,
	SubHeading,
	Button,
	Text,
	ButtonDropdownField,
} from '@forecast-it/design-system';
import {DeliverableEdges, Deliverable, Project, ProjectPhaseDeliverable} from './BaselineTypes';
import {MODAL_TYPE, showModal} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import {uniqueId} from 'lodash';
import {useIntl} from 'react-intl';

const Card = styled(FlexRow)`
	background-color: ${tokensForThemeProvider.sky.ref.color.blue[200]};
	border-radius: ${tokensForThemeProvider.sky.sys.spacing[100]};
	height: 56px;
	padding-left: 8px;
`;

const ButtonContainer = styled(FlexRow)`
	cursor: pointer;
`;

const StyledButtonContainer = styled.div`
	cursor: pointer;
	padding: 16px;
	border-top: 1px solid ${tokensForThemeProvider.sky.sys.color.border.decorative};
`;

const AddDeliverableButton = (props: {text: string}) => (
	<FlexRow>
		<Icon icon="deliverable" size="m" color="primary"></Icon>
		<SubHeading color={'primary'}>{props.text}</SubHeading>
	</FlexRow>
);

const AddDeliverableDropdownItem = (props: {onClick: () => void; text: string}) => (
	<StyledButtonContainer onClick={props.onClick}>
		<FlexRow justifyContent="">
			<SubHeading>{props.text}</SubHeading>
			<Icon icon="plus" size="m"></Icon>
		</FlexRow>
	</StyledButtonContainer>
);

type SelectDeliverableCardProps = {
	deliverables: DeliverableEdges;
	phaseId: string;
	deliverablesInPhase: ProjectPhaseDeliverable[];
	setProjectPhaseDeliverables?: React.Dispatch<React.SetStateAction<ProjectPhaseDeliverable[]>>;
	project?: Project;
	text: string;
	budgetType?: string | null;
};

export const SelectDeliverableCard = (props: SelectDeliverableCardProps): JSX.Element => {
	const {setProjectPhaseDeliverables, deliverablesInPhase, project, text} = props;

	const {formatMessage} = useIntl();

	const [editing, setEditing] = useState(false);
	const [selectedDeliverable, setSelectedDeliverable] = useState<Deliverable | null>(null);

	const usedDeliverableSet = useMemo(
		() => new Set(deliverablesInPhase.map(phaseDeliverable => phaseDeliverable.deliverable?.id).filter(Boolean)),
		[deliverablesInPhase]
	);

	const deliverables = useMemo(
		() => props.deliverables.filter(deliverable => deliverable?.node?.id && !usedDeliverableSet.has(deliverable?.node?.id)),
		[usedDeliverableSet]
	);

	const addPhaseDeliverable = (deliverable: Deliverable | null) => {
		deliverable?.id &&
			setProjectPhaseDeliverables &&
			setProjectPhaseDeliverables(previous =>
				usedDeliverableSet.has(deliverable?.id)
					? previous
					: [
							...previous,
							{
								id: uniqueId(),
								phaseId: props.phaseId,
								deliverable: {
									id: deliverable.id,
									name: deliverable.name,
								},
								financialNumbers: {
									baselineMinutes: 0,
									baselineTimeAndExpenses: 0,
									baselineRevenue: 0,
									baselineCost: 0,
									baselineProfit: 0,
									baselineMargin: 0,
								},
							},
					  ]
			);
		setSelectedDeliverable(null);
	};

	const openDeliverableCreationModal = () => {
		showModal({
			type: MODAL_TYPE.DELIVERABLE_EDITOR_MODAL,
			deliverables: props.deliverables,
			action: 'create',
			projectId: project?.id,
			onSuccess: addPhaseDeliverable,
			budgetType: project?.budgetType,
		});
	};

	return (
		<Card>
			{editing ? (
				<div style={{width: '50%'}}>
					<FlexRow gap={'s'}>
						<Icon icon="deliverable" size="m"></Icon>
						<Text>{formatMessage({id: 'common.deliverable'})}</Text>
						<DropdownList>
							<DropdownList.Trigger>
								<ButtonDropdownField>
									{selectedDeliverable ? (
										<Text ellipsis={true} size={'1'}>
											{selectedDeliverable.name}
										</Text>
									) : (
										<Text ellipsis={true} size={'1'} color="auxiliary">
											{formatMessage({id: 'deliverable.select_a_deliverable'})}
										</Text>
									)}
								</ButtonDropdownField>
							</DropdownList.Trigger>
							<DropdownList.Content offset={10} useTriggerWidth>
								{deliverables.map(deliverable => (
									<DropdownList.Item onClick={() => setSelectedDeliverable(deliverable?.node || null)}>
										{deliverable?.node?.name}
									</DropdownList.Item>
								))}
								<AddDeliverableDropdownItem
									onClick={openDeliverableCreationModal}
									text={formatMessage({id: 'deliverable.add_new_deliverable'})}
								/>
							</DropdownList.Content>
						</DropdownList>
						<Button
							emphasis={EMPHASIS.HIGH}
							disabled={!selectedDeliverable}
							onPress={() => addPhaseDeliverable(selectedDeliverable)}
						>
							{formatMessage({id: 'common.select'})}
						</Button>
						<Button emphasis={EMPHASIS.MEDIUM} onPress={() => setEditing(false)}>
							{formatMessage({id: 'common.cancel'})}
						</Button>
					</FlexRow>
				</div>
			) : (
				<ButtonContainer onClick={() => setEditing(true)}>
					<AddDeliverableButton text={text} />
				</ButtonContainer>
			)}
		</Card>
	);
};
